import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { CheckSquare, AlertCircle, Plus, Clock, TrendingUp, Activity } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface Audit {
  id: string;
  title: string;
  description: string;
  process_area: string;
  audit_type: 'internal' | 'external' | 'compliance' | 'quality';
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  start_date: string;
  end_date: string;
  auditor: string;
  score: number;
  findings: AuditFinding[];
  recommendations: string[];
  created_at: string;
}

interface AuditFinding {
  id: string;
  audit_id: string;
  finding_type: 'observation' | 'minor' | 'major' | 'critical';
  description: string;
  evidence: string;
  recommendation: string;
  corrective_action: string;
  responsible_person: string;
  due_date: string;
  status: 'open' | 'in_progress' | 'closed' | 'verified';
  created_at: string;
}

interface ProcessArea {
  id: string;
  name: string;
  description: string;
  last_audit_date: string;
  next_audit_due: string;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  compliance_score: number;
}

export default function ProcessAuditPage() {
  const [isNewAuditOpen, setIsNewAuditOpen] = useState(false);
  const [isNewFindingOpen, setIsNewFindingOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<string>("");
  const [newAudit, setNewAudit] = useState({
    title: "",
    description: "",
    process_area: "",
    audit_type: "internal",
    start_date: "",
    end_date: "",
    auditor: ""
  });
  const [newFinding, setNewFinding] = useState({
    finding_type: "observation",
    description: "",
    evidence: "",
    recommendation: "",
    responsible_person: "",
    due_date: ""
  });

  const queryClient = useQueryClient();

  const { data: audits = [] } = useQuery({
    queryKey: ['process-audits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('process_audits' as any)
        .select('*, findings:audit_findings(*)')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as Audit[]) || [];
    }
  });

  const { data: findings = [] } = useQuery({
    queryKey: ['audit-findings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_findings' as any)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as AuditFinding[]) || [];
    }
  });

  const { data: processAreas = [] } = useQuery({
    queryKey: ['process-areas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('process_areas' as any)
        .select('*')
        .order('name');
      
      if (error) throw error;
      return (data as unknown as ProcessArea[]) || [];
    }
  });

  const createAuditMutation = useMutation({
    mutationFn: async (audit: any) => {
      const { data, error } = await supabase
        .from('process_audits' as any)
        .insert([{ ...audit, status: 'planned', score: 0 }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['process-audits'] });
      setIsNewAuditOpen(false);
      setNewAudit({
        title: "", description: "", process_area: "", audit_type: "internal",
        start_date: "", end_date: "", auditor: ""
      });
      toast.success("تم إنشاء التدقيق بنجاح");
    }
  });

  const createFindingMutation = useMutation({
    mutationFn: async (finding: any) => {
      const { data, error } = await supabase
        .from('audit_findings' as any)
        .insert([{
          ...finding,
          audit_id: selectedAudit,
          status: 'open'
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit-findings'] });
      setIsNewFindingOpen(false);
      setNewFinding({
        finding_type: "observation", description: "", evidence: "",
        recommendation: "", responsible_person: "", due_date: ""
      });
      toast.success("تم إضافة الملاحظة بنجاح");
    }
  });

  const updateFindingStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('audit_findings' as any)
        .update({ status })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit-findings'] });
      toast.success("تم تحديث حالة الملاحظة");
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      case 'open': return 'bg-red-100 text-red-800';
      case 'closed': return 'bg-green-100 text-green-800';
      case 'verified': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFindingTypeColor = (type: string) => {
    switch (type) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'major': return 'bg-orange-100 text-orange-800';
      case 'minor': return 'bg-yellow-100 text-yellow-800';
      case 'observation': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const completedAudits = audits.filter(a => a.status === 'completed').length;
  const inProgressAudits = audits.filter(a => a.status === 'in_progress').length;
  const openFindings = findings.filter(f => f.status === 'open').length;
  const averageScore = audits.length > 0 
    ? audits.filter(a => a.score > 0).reduce((sum, a) => sum + a.score, 0) / audits.filter(a => a.score > 0).length 
    : 0;

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
          <Activity className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">تدقيق العمليات</h1>
          <p className="text-muted-foreground mt-1">
            مراجعة العمليات التشغيلية وضمان الجودة
          </p>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">التدقيقات المكتملة</CardTitle>
            <CheckSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{completedAudits}</div>
            <p className="text-xs text-muted-foreground">من أصل {audits.length}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">قيد التنفيذ</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{inProgressAudits}</div>
            <p className="text-xs text-muted-foreground">تدقيق نشط</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ملاحظات مفتوحة</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{openFindings}</div>
            <p className="text-xs text-muted-foreground">تحتاج معالجة</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">متوسط النقاط</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageScore.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">من أصل 100</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="audits" className="space-y-6">
        <TabsList>
          <TabsTrigger value="audits">التدقيقات</TabsTrigger>
          <TabsTrigger value="findings">الملاحظات</TabsTrigger>
          <TabsTrigger value="processes">مجالات العمليات</TabsTrigger>
        </TabsList>

        <TabsContent value="audits">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">تدقيق العمليات</h2>
            <Dialog open={isNewAuditOpen} onOpenChange={setIsNewAuditOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 ml-2" />
                  تدقيق جديد
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>إنشاء تدقيق جديد</DialogTitle>
                  <DialogDescription>
                    أدخل تفاصيل التدقيق الجديد
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">عنوان التدقيق *</label>
                    <Input
                      value={newAudit.title}
                      onChange={(e) => setNewAudit({ ...newAudit, title: e.target.value })}
                      placeholder="أدخل عنوان التدقيق"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">مجال العملية</label>
                      <Select value={newAudit.process_area} onValueChange={(value) => setNewAudit({ ...newAudit, process_area: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر المجال" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="customer_service">خدمة العملاء</SelectItem>
                          <SelectItem value="booking_process">عملية الحجز</SelectItem>
                          <SelectItem value="financial">العمليات المالية</SelectItem>
                          <SelectItem value="quality_control">ضبط الجودة</SelectItem>
                          <SelectItem value="hr_processes">العمليات البشرية</SelectItem>
                          <SelectItem value="it_systems">الأنظمة التقنية</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">نوع التدقيق</label>
                      <Select value={newAudit.audit_type} onValueChange={(value) => setNewAudit({ ...newAudit, audit_type: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="internal">داخلي</SelectItem>
                          <SelectItem value="external">خارجي</SelectItem>
                          <SelectItem value="compliance">امتثال</SelectItem>
                          <SelectItem value="quality">جودة</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">تاريخ البداية</label>
                      <Input
                        type="date"
                        value={newAudit.start_date}
                        onChange={(e) => setNewAudit({ ...newAudit, start_date: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">تاريخ النهاية</label>
                      <Input
                        type="date"
                        value={newAudit.end_date}
                        onChange={(e) => setNewAudit({ ...newAudit, end_date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">المدقق</label>
                    <Input
                      value={newAudit.auditor}
                      onChange={(e) => setNewAudit({ ...newAudit, auditor: e.target.value })}
                      placeholder="اسم المدقق المسؤول"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">وصف التدقيق</label>
                    <Textarea
                      value={newAudit.description}
                      onChange={(e) => setNewAudit({ ...newAudit, description: e.target.value })}
                      placeholder="وصف النطاق والأهداف"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={() => createAuditMutation.mutate(newAudit)}
                      disabled={!newAudit.title || createAuditMutation.isPending}
                    >
                      إنشاء التدقيق
                    </Button>
                    <Button variant="outline" onClick={() => setIsNewAuditOpen(false)}>
                      إلغاء
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {audits.map((audit) => (
              <Card key={audit.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{audit.title}</CardTitle>
                      <CardDescription>{audit.description}</CardDescription>
                      <div className="flex gap-2 mt-2">
                        <Badge className={getStatusColor(audit.status)}>
                          {audit.status === 'planned' && 'مخطط'}
                          {audit.status === 'in_progress' && 'قيد التنفيذ'}
                          {audit.status === 'completed' && 'مكتمل'}
                          {audit.status === 'cancelled' && 'ملغي'}
                        </Badge>
                        <Badge variant="outline">
                          {audit.audit_type === 'internal' && 'داخلي'}
                          {audit.audit_type === 'external' && 'خارجي'}
                          {audit.audit_type === 'compliance' && 'امتثال'}
                          {audit.audit_type === 'quality' && 'جودة'}
                        </Badge>
                        <Badge variant="outline">
                          {audit.process_area === 'customer_service' && 'خدمة العملاء'}
                          {audit.process_area === 'booking_process' && 'عملية الحجز'}
                          {audit.process_area === 'financial' && 'العمليات المالية'}
                          {audit.process_area === 'quality_control' && 'ضبط الجودة'}
                          {audit.process_area === 'hr_processes' && 'العمليات البشرية'}
                          {audit.process_area === 'it_systems' && 'الأنظمة التقنية'}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-left">
                      {audit.score > 0 && (
                        <div className="text-2xl font-bold">{audit.score}</div>
                      )}
                      <div className="text-sm text-muted-foreground">
                        {audit.start_date} - {audit.end_date}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        المدقق: {audit.auditor}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-muted-foreground">
                      الملاحظات: {audit.findings?.length || 0}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedAudit(audit.id);
                        setIsNewFindingOpen(true);
                      }}
                    >
                      إضافة ملاحظة
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="findings">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">ملاحظات التدقيق</h2>
          </div>

          <div className="grid gap-4">
            {findings.map((finding) => {
              const audit = audits.find(a => a.id === finding.audit_id);
              return (
                <Card key={finding.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg mb-1">
                          {audit?.title} - ملاحظة
                        </CardTitle>
                        <CardDescription>{finding.description}</CardDescription>
                        <div className="flex gap-2 mt-2">
                          <Badge className={getFindingTypeColor(finding.finding_type)}>
                            {finding.finding_type === 'critical' && 'حرجة'}
                            {finding.finding_type === 'major' && 'رئيسية'}
                            {finding.finding_type === 'minor' && 'ثانوية'}
                            {finding.finding_type === 'observation' && 'ملاحظة'}
                          </Badge>
                          <Badge className={getStatusColor(finding.status)}>
                            {finding.status === 'open' && 'مفتوحة'}
                            {finding.status === 'in_progress' && 'قيد المعالجة'}
                            {finding.status === 'closed' && 'مغلقة'}
                            {finding.status === 'verified' && 'تم التحقق'}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-left">
                        {finding.due_date && (
                          <div className="text-sm text-muted-foreground">
                            الموعد النهائي: {new Date(finding.due_date).toLocaleDateString('ar-SA')}
                          </div>
                        )}
                        {finding.responsible_person && (
                          <div className="text-sm text-muted-foreground">
                            المسؤول: {finding.responsible_person}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {finding.evidence && (
                        <div>
                          <span className="text-sm font-medium">الدليل: </span>
                          <span className="text-sm text-muted-foreground">{finding.evidence}</span>
                        </div>
                      )}
                      {finding.recommendation && (
                        <div>
                          <span className="text-sm font-medium">التوصية: </span>
                          <span className="text-sm text-muted-foreground">{finding.recommendation}</span>
                        </div>
                      )}
                      {finding.corrective_action && (
                        <div>
                          <span className="text-sm font-medium">الإجراء التصحيحي: </span>
                          <span className="text-sm text-muted-foreground">{finding.corrective_action}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end mt-4">
                      <Select 
                        value={finding.status} 
                        onValueChange={(value) => updateFindingStatusMutation.mutate({ id: finding.id, status: value })}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">مفتوحة</SelectItem>
                          <SelectItem value="in_progress">قيد المعالجة</SelectItem>
                          <SelectItem value="closed">مغلقة</SelectItem>
                          <SelectItem value="verified">تم التحقق</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="processes">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">مجالات العمليات</h2>
          </div>

          <div className="grid gap-4">
            {processAreas.map((area) => (
              <Card key={area.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{area.name}</CardTitle>
                      <CardDescription>{area.description}</CardDescription>
                      <div className="flex gap-2 mt-2">
                        <Badge className={getRiskLevelColor(area.risk_level)}>
                          {area.risk_level === 'critical' && 'حرج'}
                          {area.risk_level === 'high' && 'عالي'}
                          {area.risk_level === 'medium' && 'متوسط'}
                          {area.risk_level === 'low' && 'منخفض'}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="text-2xl font-bold">{area.compliance_score}%</div>
                      <div className="text-sm text-muted-foreground">نقاط الامتثال</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>مستوى الامتثال</span>
                        <span>{area.compliance_score}%</span>
                      </div>
                      <Progress value={area.compliance_score} className="h-2" />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">آخر تدقيق: </span>
                        <span>{area.last_audit_date ? new Date(area.last_audit_date).toLocaleDateString('ar-SA') : 'لم يتم'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">التدقيق القادم: </span>
                        <span>{area.next_audit_due ? new Date(area.next_audit_due).toLocaleDateString('ar-SA') : 'غير محدد'}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* New Finding Dialog */}
      <Dialog open={isNewFindingOpen} onOpenChange={setIsNewFindingOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>إضافة ملاحظة تدقيق</DialogTitle>
            <DialogDescription>
              أدخل تفاصيل الملاحظة أو المخالفة المكتشفة
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">نوع الملاحظة</label>
              <Select value={newFinding.finding_type} onValueChange={(value) => setNewFinding({ ...newFinding, finding_type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="observation">ملاحظة</SelectItem>
                  <SelectItem value="minor">ثانوية</SelectItem>
                  <SelectItem value="major">رئيسية</SelectItem>
                  <SelectItem value="critical">حرجة</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">وصف الملاحظة *</label>
              <Textarea
                value={newFinding.description}
                onChange={(e) => setNewFinding({ ...newFinding, description: e.target.value })}
                placeholder="وصف تفصيلي للملاحظة أو المخالفة"
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">الدليل</label>
              <Textarea
                value={newFinding.evidence}
                onChange={(e) => setNewFinding({ ...newFinding, evidence: e.target.value })}
                placeholder="الأدلة والمستندات المرتبطة"
                rows={2}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">التوصية</label>
              <Textarea
                value={newFinding.recommendation}
                onChange={(e) => setNewFinding({ ...newFinding, recommendation: e.target.value })}
                placeholder="التوصية للمعالجة"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">المسؤول</label>
                <Input
                  value={newFinding.responsible_person}
                  onChange={(e) => setNewFinding({ ...newFinding, responsible_person: e.target.value })}
                  placeholder="الشخص المسؤول عن المعالجة"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">الموعد النهائي</label>
                <Input
                  type="date"
                  value={newFinding.due_date}
                  onChange={(e) => setNewFinding({ ...newFinding, due_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                onClick={() => createFindingMutation.mutate(newFinding)}
                disabled={!newFinding.description || !selectedAudit || createFindingMutation.isPending}
              >
                إضافة الملاحظة
              </Button>
              <Button variant="outline" onClick={() => setIsNewFindingOpen(false)}>
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}